<template>
  <div class="boss-page performance-config">
    <van-nav-bar title="制定绩效标准" left-arrow @click-left="$router.back()"/>
    <van-skeleton v-if="loading" title :row="8"/>
    <template v-else>
      <section class="step">
        <header><i>1</i>
          <div><b>选择岗位</b><small>同一岗位默认使用同一套标准</small></div>
        </header>
        <div class="position-grid">
          <button v-for="item in positions" :key="item.family" :class="{active:form.positionFamily===item.family}" @click="selectPosition(item)">
            <b>{{ item.name }}</b><small>{{ item.count }}人</small>
          </button>
        </div>
      </section>
      <section v-if="form.positionFamily" class="step">
        <header><i>2</i>
          <div><b>适用范围</b><small>需要区别考核时，再指定某位员工</small></div>
        </header>
        <div class="segments">
          <button :class="{active:!personal}" @click="personal=false;form.employeeNo=''">岗位全部员工</button>
          <button :class="{active:personal}" @click="personal=true">指定一名员工</button>
        </div>
        <div v-if="personal" class="employee-list">
          <button v-for="item in positionEmployees" :key="item.employeeNo" :class="{active:form.employeeNo===item.employeeNo}"
                  @click="form.employeeNo=item.employeeNo">
            <span><b>{{ item.employeeName }}</b><small>{{ item.departmentName }}</small></span>
            <van-icon :name="form.employeeNo===item.employeeNo?'checked':'circle'"/>
          </button>
        </div>
      </section>
      <section v-if="form.positionFamily" class="step metrics-step">
        <header>
          <i>3</i>
          <div>
            <b>设置考核指标</b>
            <small>量化与非量化可同时配置；改一项权重时其余自动分配，合计始终 100%</small>
          </div>
          <strong>{{ weightTotal }}%</strong>
        </header>
        <article v-for="(metric,index) in form.metrics" :key="index" class="metric">
          <div class="metric-title"><b>指标 {{ index + 1 }}</b>
            <button v-if="form.metrics.length>1" @click="removeMetric(index)">
              <van-icon name="delete-o"/>
            </button>
          </div>
          <div class="metric-types">
            <button :class="{active:metric.metricType==='QUANTITATIVE'}" @click="metric.metricType='QUANTITATIVE'">量化指标</button>
            <button :class="{active:metric.metricType==='QUALITATIVE'}" @click="setQualitative(metric)">非量化指标</button>
          </div>
          <label><span>指标名称</span><input v-model.trim="metric.name"
                                             :placeholder="metric.metricType==='QUANTITATIVE'?'例如：月销售额':'例如：交付质量'"/></label>
          <div v-if="metric.metricType==='QUANTITATIVE'" class="inline three">
            <label><span>月目标</span><input v-model.number="metric.monthlyTarget" type="number" min="0" placeholder="100"/></label>
            <label><span>单位</span><input v-model.trim="metric.unit" placeholder="万元/人/次"/></label>
            <label><span>权重</span>
              <div class="suffix"><input :value="metric.weightPercent" type="number" min="1" :max="maxWeight(index)"
                                         @change="onWeightChange(index, $event.target.value)" @blur="onWeightChange(index, $event.target.value)"/><em>%</em>
              </div>
            </label>
          </div>
          <div v-else class="inline one">
            <label><span>权重</span>
              <div class="suffix"><input :value="metric.weightPercent" type="number" min="1" :max="maxWeight(index)"
                                         @change="onWeightChange(index, $event.target.value)" @blur="onWeightChange(index, $event.target.value)"/><em>%</em>
              </div>
            </label>
          </div>
          <label>
            <span>{{ metric.metricType === 'QUALITATIVE' ? '考核标准' : '计分说明' }} <small>{{
                metric.metricType === 'QUALITATIVE' ? '必填' : '选填'
              }}</small></span>
            <input v-model.trim="metric.scoringRule"
                   :placeholder="metric.metricType==='QUALITATIVE'?'例如：方案完整、按期交付、证据可核验':'默认按完成率计分，达标得满分'"/>
          </label>
        </article>
        <button class="add-metric" @click="addMetric">
          <van-icon name="plus"/>
          添加指标
        </button>
        <p class="weight-hint">权重合计固定 100%，其余指标会自动补齐</p>
        <label class="effective"><span>生效日期</span><input v-model="form.effectiveFrom" type="date"/></label>
      </section>
      <div class="bottom-space"></div>
      <footer v-if="form.positionFamily">
        <button :disabled="saving" @click="publish">{{ saving ? '正在发布...' : '确认并发布' }}</button>
      </footer>
    </template>
  </div>
</template>

<script>
/**
 * Boss 制定绩效标准：与运营端共用 KPI 版本表。
 * 权重始终自动补齐为 100%：改一项时，其余指标均分剩余；新增/删除时整体均分。
 */
import coreapi from '@/utils/coreapi';

const today = () => new Date().toISOString().slice(0, 10);
const emptyMetric = weight => ({metricType: 'QUANTITATIVE', name: '', monthlyTarget: null, unit: '', weightPercent: weight, scoringRule: ''});
export default {
  data() {
    return {
      loading: true,
      saving: false,
      personal: false,
      employees: [],
      form: {positionFamily: '', positionName: '', employeeNo: '', effectiveFrom: today(), metrics: [emptyMetric(100)]}
    }
  },
  computed: {
    positions() {
      const map = {};
      this.employees.forEach(e => {
        const family = e.positionFamily || 'GENERAL';
        if (!map[family]) map[family] = {family, name: e.positionName || family, count: 0};
        map[family].count++
      });
      return Object.values(map)
    },
    positionEmployees() {
      return this.employees.filter(e => (e.positionFamily || 'GENERAL') === this.form.positionFamily)
    },
    weightTotal() {
      return this.form.metrics.reduce((sum, item) => sum + Number(item.weightPercent || 0), 0)
    }
  },
  async mounted() {
    try {
      this.employees = await coreapi.bossEmployees()
    } catch (e) {
      this.$toast.fail(e.message)
    } finally {
      this.loading = false
    }
  },
  methods: {
    selectPosition(item) {
      this.form.positionFamily = item.family;
      this.form.positionName = item.name;
      this.form.employeeNo = '';
      this.personal = false;
      this.form.metrics = [emptyMetric(100)]
    },
    setQualitative(metric) {
      metric.metricType = 'QUALITATIVE';
      metric.monthlyTarget = null;
      metric.unit = ''
    },
    maxWeight(index) {
      const n = this.form.metrics.length;
      return n <= 1 ? 100 : 100 - (n - 1)
    },
    /** 各项权重均分到合计 100（整数百分比，余数摊到前面几项） */
    redistributeEqual() {
      const list = this.form.metrics, n = list.length;
      if (!n) return;
      const base = Math.floor(100 / n);
      let left = 100 - base * n;
      list.forEach(m => {
        m.weightPercent = base + (left > 0 ? 1 : 0);
        if (left > 0) left -= 1
      });
    },
    /** 固定当前项权重，其余均分剩余，保证合计 100 */
    onWeightChange(index, raw) {
      const list = this.form.metrics, n = list.length;
      if (!n) return;
      if (n === 1) {
        list[0].weightPercent = 100;
        return
      }
      let w = Math.round(Number(raw));
      if (!Number.isFinite(w)) w = list[index].weightPercent || 1;
      const max = 100 - (n - 1);
      w = Math.min(Math.max(w, 1), max);
      list[index].weightPercent = w;
      const rest = 100 - w, others = n - 1;
      const base = Math.floor(rest / others);
      let left = rest - base * others;
      list.forEach((m, i) => {
        if (i === index) return;
        m.weightPercent = base + (left > 0 ? 1 : 0);
        if (left > 0) left -= 1;
      });
    },
    addMetric() {
      this.form.metrics.push(emptyMetric(0));
      this.redistributeEqual()
    },
    removeMetric(index) {
      this.form.metrics.splice(index, 1);
      this.redistributeEqual()
    },
    async publish() {
      if (this.personal && !this.form.employeeNo) return this.$toast('请选择员工');
      if (this.weightTotal !== 100 && this.form.metrics.length) {
        this.onWeightChange(0, this.form.metrics[0].weightPercent);
      }
      if (this.form.metrics.some(m => !m.name || !m.weightPercent || (m.metricType === 'QUANTITATIVE' && !m.monthlyTarget) || (m.metricType === 'QUALITATIVE' && !m.scoringRule))) return this.$toast('请完整填写指标目标或非量化标准');
      if (this.weightTotal !== 100) return this.$toast('指标权重合计必须为100%');
      const target = this.personal ? this.positionEmployees.find(e => e.employeeNo === this.form.employeeNo) : null;
      try {
        await this.$dialog.confirm({
          title: '确认发布',
          message: `${this.form.positionName} · ${target ? target.employeeName : '岗位全部员工'}\n共${this.form.metrics.length}项指标，${this.form.effectiveFrom}生效`,
          confirmButtonText: '发布标准',
          confirmButtonColor: '#B71C1C'
        })
      } catch (ignore) {
        return
      }
      this.saving = true;
      try {
        await coreapi.publishBossPerformanceConfig(this.form);
        this.$toast.success('绩效标准已发布');
        this.$router.back()
      } catch (e) {
        this.$toast.fail(e.message)
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style lang="less" scoped>
@import './boss.less';

.performance-config {
  min-height: 100vh;
  background: #f5f6f8
}

.step {
  margin: .22rem 0;
  padding: .32rem .28rem;
  background: #fff;
  border-top: 1PX solid #eee;
  border-bottom: 1PX solid #eee
}

.step > header {
  display: flex;
  align-items: center;
  gap: .18rem;
  margin-bottom: .26rem
}

.step > header > i {
  display: flex;
  align-items: center;
  justify-content: center;
  width: .48rem;
  height: .48rem;
  color: #fff;
  background: #B71C1C;
  border-radius: 50%;
  font-style: normal;
  font-weight: 700
}

.step > header > div {
  display: flex;
  flex: 1;
  flex-direction: column
}

.step > header b {
  font-size: .32rem
}

.step > header small {
  margin-top: .05rem;
  color: #8a8d99;
  font-size: .23rem
}

.step > header strong {
  color: #B71C1C;
  font-size: .34rem
}

.position-grid {
  display: grid;
  grid-template-columns:repeat(2, minmax(0, 1fr));
  gap: .16rem
}

.position-grid button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: .86rem;
  padding: 0 .22rem;
  border: 1PX solid #ddd;
  background: #fff;
  border-radius: .12rem
}

.position-grid button.active, .segments button.active {
  color: #B71C1C;
  border-color: #B71C1C;
  background: #fff7f7
}

.position-grid b {
  font-size: .27rem
}

.position-grid small {
  color: #8b8e99
}

.segments {
  display: grid;
  grid-template-columns:1fr 1fr;
  gap: .12rem
}

.segments button {
  height: .72rem;
  border: 1PX solid #ddd;
  background: #f8f8f8;
  border-radius: .1rem;
  font-size: .26rem
}

.employee-list {
  margin-top: .16rem;
  border-top: 1PX solid #eee
}

.employee-list button {
  display: flex;
  align-items: center;
  width: 100%;
  padding: .2rem 0;
  border: 0;
  border-bottom: 1PX solid #eee;
  background: #fff;
  text-align: left
}

.employee-list span {
  display: flex;
  flex: 1;
  flex-direction: column
}

.employee-list b {
  font-size: .27rem
}

.employee-list small {
  margin-top: .04rem;
  color: #888
}

.employee-list .van-icon {
  color: #aaa;
  font-size: .34rem
}

.employee-list button.active .van-icon {
  color: #B71C1C
}

.metric {
  margin-top: .18rem;
  padding: .22rem;
  border: 1PX solid #e3e3e3;
  border-radius: .12rem
}

.metric-title {
  display: flex;
  justify-content: space-between;
  margin-bottom: .16rem
}

.metric-title b {
  font-size: .28rem
}

.metric-title button {
  border: 0;
  color: #999;
  background: transparent;
  font-size: .32rem
}

.metric label, .effective {
  display: block;
  margin-top: .16rem
}

.metric label > span, .effective > span {
  display: block;
  margin-bottom: .08rem;
  color: #555;
  font-size: .23rem
}

.metric label > span small {
  color: #999;
  font-size: .2rem
}

.metric input, .effective input {
  box-sizing: border-box;
  width: 100%;
  height: .7rem;
  padding: 0 .16rem;
  border: 1PX solid #ddd;
  border-radius: .08rem;
  background: #fff;
  font-size: .26rem
}

.inline {
  display: grid;
  gap: .12rem
}

.inline.three {
  grid-template-columns:1.2fr .9fr .8fr
}

.inline.one {
  grid-template-columns:1fr
}

.suffix {
  position: relative
}

.suffix input {
  padding-right: .38rem
}

.suffix em {
  position: absolute;
  right: .12rem;
  top: .18rem;
  color: #888;
  font-style: normal
}

.add-metric {
  width: 100%;
  height: .72rem;
  margin-top: .18rem;
  border: 1PX dashed #B71C1C;
  color: #B71C1C;
  background: #fff;
  border-radius: .1rem;
  font-size: .26rem
}

.weight-hint {
  margin: .16rem 0 0;
  color: #8a8d99;
  font-size: .23rem
}

.effective {
  margin-top: .24rem
}

.bottom-space {
  height: 1.3rem
}

footer {
  position: fixed;
  z-index: 3;
  right: 0;
  bottom: 0;
  left: 0;
  padding: .18rem .28rem calc(.18rem + env(safe-area-inset-bottom));
  background: #fff;
  box-shadow: 0 -.06rem .2rem rgba(0, 0, 0, .08)
}

footer button {
  width: 100%;
  height: .82rem;
  border: 0;
  color: #fff;
  background: #B71C1C;
  border-radius: .12rem;
  font-size: .3rem;
  font-weight: 600
}

footer button:disabled {
  opacity: .55
}

.metric-types {
  display: grid;
  grid-template-columns:1fr 1fr;
  gap: .1rem
}

.metric-types button {
  height: .58rem;
  border: 1PX solid #ddd;
  background: #f8f8f8;
  border-radius: .08rem;
  color: #666
}

.metric-types button.active {
  border-color: #B71C1C;
  color: #B71C1C;
  background: #fff7f7
}
</style>



// WEBPACK FOOTER //
// src/views/boss/PerformanceConfig.vue